import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';

import { queryKeys } from '~/api/queryKeys';
import { extractApiError } from '~/utils/extract-api-error';

import {
  deleteSyncProduct,
  getDefaultGroupingConfig,
  getItemGroupChildren,
  getProductColumns,
  getProductDetail,
  getProductFilterCatalog,
  getProductSearchFields,
  getSyncCapabilities,
  listCategories,
  listCatalogProducts,
  listItemGroups,
  listProductRevisions,
  updateSyncProduct,
} from '../services/products-catalog.service';
import type {
  CategoriesListRequest,
  CategoriesListResponse,
  DefaultGroupingConfigResponse,
  ItemGroupChildrenRequest,
  ItemGroupChildrenResponse,
  ItemGroupsListRequest,
  ItemGroupsListResponse,
  ProductColumnsResponse,
  ProductDetailResponse,
  ProductFiltersRequest,
  ProductFiltersResponse,
  ProductRevisionsRequest,
  ProductRevisionsResponse,
  ProductSearchFieldsResponse,
  ProductsListRequest,
  ProductsListResponse,
  SyncCapabilitiesResponse,
  SyncProductDeleteRequest,
  SyncProductRequest,
  SyncProductResponse,
} from '../types/products-catalog.types';

const STALE_30S = 30 * 1000;
const STALE_5MIN = 5 * 60 * 1000;

/** Shared retry predicate — client errors (4xx) are terminal, so only retry
 *  transient failures once. */
function noRetryOn4xx(failureCount: number, error: unknown): boolean {
  const status = (error as { response?: { status?: number } }).response?.status;
  if (status === 400 || status === 401 || status === 403 || status === 404) return false;
  return failureCount < 1;
}

/** Fetches a connection's paginated product catalog. Disabled until a
 *  connection is selected; keeps the previous page while the next loads. */
export function useCatalogProducts(
  params: ProductsListRequest,
  subsidiaryId?: string,
  enabled = true,
) {
  return useQuery<ProductsListResponse>({
    queryKey: queryKeys.products.list(params.connectionId, {
      syncId: params.syncId,
      ingestionDate: params.ingestionDate,
      offset: params.offset,
      limit: params.limit,
      sortBy: params.sortBy,
      filters: params.filters,
      search: params.search,
      subsidiaryId,
    }),
    queryFn: () => listCatalogProducts(params, subsidiaryId),
    enabled: enabled && !!params.connectionId,
    staleTime: STALE_30S,
    placeholderData: (prev) => prev,
    retry: noRetryOn4xx,
  });
}

/** Params for the infinite (card-view) product list — everything except the
 *  offset, which the infinite query supplies as the page cursor. */
type InfiniteProductsParams = Omit<ProductsListRequest, 'offset' | 'limit'>;

/** Infinite product list for the card view — appends pages of `pageSize`
 *  (backend caps a single request at 100) as the user scrolls. Disabled until a
 *  connection is selected; keeps prior pages while the next loads. */
export function useInfiniteCatalogProducts(
  params: InfiniteProductsParams,
  pageSize: number,
  subsidiaryId?: string,
  enabled = true,
) {
  return useInfiniteQuery<ProductsListResponse>({
    queryKey: queryKeys.products.infiniteList(params.connectionId, {
      syncId: params.syncId,
      ingestionDate: params.ingestionDate,
      pageSize,
      sortBy: params.sortBy,
      filters: params.filters,
      search: params.search,
      subsidiaryId,
    }),
    queryFn: ({ pageParam }) =>
      listCatalogProducts(
        { ...params, offset: pageParam as number, limit: pageSize },
        subsidiaryId,
      ),
    initialPageParam: 0,
    getNextPageParam: (lastPage, allPages) => {
      const total = lastPage.data.pagination.total;
      const loaded = allPages.reduce((sum, pg) => sum + pg.data.products.length, 0);
      return loaded < total ? loaded : undefined;
    },
    enabled: enabled && !!params.connectionId,
    staleTime: STALE_30S,
    retry: noRetryOn4xx,
  });
}

/** Fetches the catalog search field options for a connector (static metadata,
 *  so cached for 5 min). Disabled until a connector is known. */
export function useProductSearchFields(
  connectorCode: string,
  subsidiaryId?: string,
  enabled = true,
) {
  return useQuery<ProductSearchFieldsResponse>({
    queryKey: queryKeys.products.searchFields(connectorCode, subsidiaryId),
    queryFn: () => getProductSearchFields(connectorCode, subsidiaryId),
    enabled: enabled && !!connectorCode,
    staleTime: STALE_5MIN,
    retry: noRetryOn4xx,
  });
}

/** Fetches full detail for a single product. Disabled until both a connection
 *  and an external id are known. */
export function useProductDetail(
  connectionId: string,
  externalId: string,
  subsidiaryId?: string,
  enabled = true,
) {
  return useQuery<ProductDetailResponse>({
    queryKey: queryKeys.products.detail(connectionId, externalId),
    queryFn: () => getProductDetail(connectionId, externalId, subsidiaryId),
    enabled: enabled && !!connectionId && !!externalId,
    staleTime: STALE_30S,
    retry: noRetryOn4xx,
  });
}

/** Fetches the ingestion revisions for the revision selector. Disabled until a
 *  connection is selected. */
export function useProductRevisions(
  params: ProductRevisionsRequest,
  subsidiaryId?: string,
  enabled = true,
) {
  return useQuery<ProductRevisionsResponse>({
    queryKey: queryKeys.products.revisions(params.connectionId),
    queryFn: () => listProductRevisions(params, subsidiaryId),
    enabled: enabled && !!params.connectionId,
    staleTime: STALE_30S,
    retry: noRetryOn4xx,
  });
}

/** Fetches the dynamic filter catalog (vendor options). Disabled until both a
 *  connection and connector code are known. */
export function useProductFilterCatalog(
  params: ProductFiltersRequest,
  subsidiaryId?: string,
  enabled = true,
) {
  return useQuery<ProductFiltersResponse>({
    queryKey: queryKeys.products.filterCatalog(
      params.connectorCode,
      params.connectionId,
      params.syncId,
      params.appliedFilters ?? {},
    ),
    queryFn: () => getProductFilterCatalog(params, subsidiaryId),
    enabled: enabled && !!params.connectionId && !!params.connectorCode,
    staleTime: STALE_30S,
    retry: noRetryOn4xx,
  });
}

/** Fetches the connector column metadata. Disabled until a connector code is
 *  known; cached longer since the schema rarely changes. */
export function useProductColumns(connectorCode: string, subsidiaryId?: string, enabled = true) {
  return useQuery<ProductColumnsResponse>({
    // Distinct from experiments' `products.columns` key: this hook resolves the
    // full response envelope (not a bare array) and is subsidiary-scoped. Sharing
    // the key let an envelope object leak into the columns drawer and crash it.
    queryKey: queryKeys.products.catalogColumns(connectorCode, subsidiaryId),
    queryFn: () => getProductColumns(connectorCode, subsidiaryId),
    enabled: enabled && !!connectorCode,
    staleTime: STALE_5MIN,
    retry: noRetryOn4xx,
  });
}

/** Fetches the connector's default grouping-method options. Disabled until a
 *  connector code is known; cached longer since it rarely changes. */
export function useDefaultGroupingConfig(
  connectorCode: string,
  subsidiaryId?: string,
  enabled = true,
) {
  return useQuery<DefaultGroupingConfigResponse>({
    queryKey: queryKeys.products.defaultGroupingConfig(connectorCode),
    queryFn: () => getDefaultGroupingConfig(connectorCode, subsidiaryId),
    enabled: enabled && !!connectorCode,
    staleTime: STALE_5MIN,
    retry: noRetryOn4xx,
  });
}

/** Fetches the write capabilities (update/delete) for a connection. Disabled
 *  until a connection is selected. */
export function useSyncCapabilities(connectionId: string, subsidiaryId?: string, enabled = true) {
  return useQuery<SyncCapabilitiesResponse>({
    queryKey: queryKeys.syncOps.capabilities(connectionId),
    queryFn: () => getSyncCapabilities(connectionId, subsidiaryId),
    enabled: enabled && !!connectionId,
    staleTime: STALE_5MIN,
    retry: noRetryOn4xx,
  });
}

/** Fetches the paginated category breakdown. Disabled until a connection is
 *  selected; keeps the previous page while the next loads. */
export function useCategories(
  params: CategoriesListRequest,
  subsidiaryId?: string,
  enabled = true,
) {
  return useQuery<CategoriesListResponse>({
    queryKey: queryKeys.products.categories(params.connectionId, params.offset, params.limit, {
      syncId: params.syncId,
      ingestionDate: params.ingestionDate,
      sortBy: params.sortBy,
      filters: params.filters,
      subsidiaryId,
    }),
    queryFn: () => listCategories(params, subsidiaryId),
    enabled: enabled && !!params.connectionId,
    staleTime: STALE_30S,
    placeholderData: (prev) => prev,
    retry: noRetryOn4xx,
  });
}

/** Fetches the paginated item-group summaries. Disabled until a connection is
 *  selected; keeps the previous page while the next loads. */
export function useItemGroups(
  params: ItemGroupsListRequest,
  subsidiaryId?: string,
  enabled = true,
) {
  return useQuery<ItemGroupsListResponse>({
    queryKey: queryKeys.products.itemGroups(params.connectionId, params.offset, params.limit, {
      matchBy: params.matchBy,
      syncId: params.syncId,
      ingestionDate: params.ingestionDate,
      sortBy: params.sortBy,
      filters: params.filters,
      subsidiaryId,
    }),
    queryFn: () => listItemGroups(params, subsidiaryId),
    enabled: enabled && !!params.connectionId,
    staleTime: STALE_30S,
    placeholderData: (prev) => prev,
    retry: noRetryOn4xx,
  });
}

/** Fetches the parent + child variants for one item group. Disabled until both
 *  a connection and item-group id are known; keeps the previous page loaded. */
export function useItemGroupChildren(
  params: ItemGroupChildrenRequest,
  subsidiaryId?: string,
  enabled = true,
) {
  return useQuery<ItemGroupChildrenResponse>({
    queryKey: queryKeys.products.itemGroupChildren(
      params.connectionId,
      params.itemGroupId,
      params.offset,
      params.limit,
      {
        matchBy: params.matchBy,
        syncId: params.syncId,
        ingestionDate: params.ingestionDate,
        subsidiaryId,
      },
    ),
    queryFn: () => getItemGroupChildren(params, subsidiaryId),
    enabled: enabled && !!params.connectionId && !!params.itemGroupId,
    staleTime: STALE_30S,
    placeholderData: (prev) => prev,
    retry: noRetryOn4xx,
  });
}

/** Invalidates both the sync-ops (write) and products (read) caches so freshly
 *  updated/deleted rows resurface after a mutation. */
function invalidateProductCaches(qc: ReturnType<typeof useQueryClient>): void {
  void qc.invalidateQueries({ queryKey: queryKeys.syncOps.all });
  void qc.invalidateQueries({ queryKey: queryKeys.products.all });
}

/** Pushes a single product update to the source connection; toasts the outcome
 *  and refreshes the products/sync-ops caches. */
export function useUpdateSyncProduct(subsidiaryId?: string) {
  const qc = useQueryClient();

  return useMutation<SyncProductResponse, unknown, SyncProductRequest>({
    mutationFn: (params) => updateSyncProduct(params, subsidiaryId),
    onSuccess: () => {
      toast.success('Product updated.');
    },
    onError: (err) => {
      toast.error(extractApiError(err, 'Failed to update product.'));
    },
    onSettled: () => {
      invalidateProductCaches(qc);
    },
  });
}

/** Deletes a single product from the source connection; toasts the outcome and
 *  refreshes the products/sync-ops caches. */
export function useDeleteSyncProduct(subsidiaryId?: string) {
  const qc = useQueryClient();

  return useMutation<SyncProductResponse, unknown, SyncProductDeleteRequest>({
    mutationFn: (params) => deleteSyncProduct(params, subsidiaryId),
    onSuccess: () => {
      toast.success('Product deleted.');
    },
    onError: (err) => {
      toast.error(extractApiError(err, 'Failed to delete product.'));
    },
    onSettled: () => {
      invalidateProductCaches(qc);
    },
  });
}
